// ----------------------------------------------------------------------
// Excel (.xlsx) sample sheets through exceljs. The library is loaded with a dynamic
// import the first time a workbook is read or written, so it stays out of the main
// bundle. Reading turns every visible worksheet into rows of trimmed text cells,
// the shape the CSV parser produces; writing builds the sample sheet template.
// ----------------------------------------------------------------------

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// Limits for a sample sheet: far above a real batch, low enough to keep a stray
// workbook from freezing the tab.
const MAX_ROWS = 5000
const MAX_COLUMNS = 60

let loading = null

/** exceljs, loaded once. A failed load is retried on the next call. */
export const loadExcel = () => {
  if (!loading) {
    loading = import(/* webpackChunkName: "exceljs" */ 'exceljs')
      .then((mod) => (mod && mod.Workbook ? mod : mod.default))
      .catch((error) => {
        loading = null
        throw error
      })
  }
  return loading
}

const isDate = (value) => Object.prototype.toString.call(value) === '[object Date]'

const pad = (n) => String(n).padStart(2, '0')

// Excel keeps numbers as doubles: 34.5 stays 34.5, and 0.1 + 0.2 reads 0.3.
const numberText = (value) =>
  Number.isInteger(value) ? String(value) : String(Number.parseFloat(value.toPrecision(12)))

/**
 * The text of one exceljs cell value: rich text joined, a formula's cached result,
 * a hyperlink's text, a date as YYYY-MM-DD, errors as blank.
 */
export const cellText = (value) => {
  if (value === null || value === undefined) return ''
  if (isDate(value)) {
    if (Number.isNaN(value.getTime())) return ''
    return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`
  }
  if (typeof value === 'number') return Number.isFinite(value) ? numberText(value) : ''
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) return value.richText.map((run) => run.text || '').join('')
    if (Object.prototype.hasOwnProperty.call(value, 'result')) return cellText(value.result)
    if (Object.prototype.hasOwnProperty.call(value, 'text')) return cellText(value.text)
    return ''
  }
  return String(value)
}

/** A worksheet as rows of trimmed text, trailing empty rows and columns dropped. */
export const worksheetRows = (worksheet) => {
  const rowCount = Math.min(worksheet.rowCount || 0, MAX_ROWS)
  const columnCount = Math.min(worksheet.columnCount || 0, MAX_COLUMNS)
  const rows = []
  for (let r = 1; r <= rowCount; r += 1) {
    const row = worksheet.findRow(r)
    const cells = []
    for (let c = 1; c <= columnCount; c += 1) {
      cells.push(row ? cellText(row.getCell(c).value).trim() : '')
    }
    rows.push(cells)
  }
  while (rows.length && rows[rows.length - 1].every((c) => !c)) rows.pop()
  let width = 0
  rows.forEach((cells) => {
    for (let c = cells.length; c > width; c -= 1) {
      if (cells[c - 1]) {
        width = c
        break
      }
    }
  })
  return rows.map((cells) => cells.slice(0, width))
}

/**
 * Reads an .xlsx workbook.
 * @param {ArrayBuffer} buffer
 * @returns {Promise<Array<{ name: string, rows: string[][] }>>} the visible
 *   worksheets that hold at least one value, in workbook order
 */
export const readXlsx = async (buffer) => {
  const ExcelJS = await loadExcel()
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(buffer)
  const sheets = []
  workbook.eachSheet((worksheet) => {
    if (worksheet.state === 'hidden' || worksheet.state === 'veryHidden') return
    const rows = worksheetRows(worksheet)
    if (rows.some((cells) => cells.some(Boolean))) sheets.push({ name: worksheet.name, rows })
  })
  return sheets
}

/**
 * Writes a workbook from plain sheet specs.
 * @param {Array<{
 *   name: string,
 *   columns: Array<{ header: string, width?: number, text?: boolean,
 *     list?: { values: string[], prompt?: string, error?: string } }>,
 *   rows: Array<Array<string|number>>,
 *   frozenHeader?: boolean,
 *   validationRows?: number,
 * }>} sheets
 * @returns {Promise<ArrayBuffer>} the .xlsx bytes
 */
export const writeXlsxBuffer = async (sheets) => {
  const ExcelJS = await loadExcel()
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'Genesus'
  workbook.created = new Date()
  sheets.forEach((spec) => {
    const worksheet = workbook.addWorksheet(spec.name, {
      views: spec.frozenHeader ? [{ state: 'frozen', xSplit: 0, ySplit: 1 }] : [],
    })
    worksheet.columns = spec.columns.map((column, i) => ({
      header: column.header,
      key: `c${i}`,
      width: column.width || 16,
      ...(column.text ? { style: { numFmt: '@' } } : {}),
    }))
    const header = worksheet.getRow(1)
    header.font = { bold: true }
    header.border = { bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } } }
    spec.rows.forEach((values) => worksheet.addRow(values))
    // Dropdowns cover the filled rows and room below them for more.
    const lastRow = Math.max(spec.rows.length + 1, spec.validationRows || 0)
    spec.columns.forEach((column, i) => {
      if (!column.list) return
      const validation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${column.list.values.join(',')}"`],
        showErrorMessage: true,
        // A warning, not a stop: a karyotype or "Evet" still goes in.
        errorStyle: 'warning',
        errorTitle: column.header,
        error: column.list.error || `Pick ${column.list.values.join(', ')}.`,
        ...(column.list.prompt
          ? { showInputMessage: true, promptTitle: column.header, prompt: column.list.prompt }
          : {}),
      }
      for (let r = 2; r <= lastRow; r += 1) worksheet.getCell(r, i + 1).dataValidation = validation
    })
  })
  return workbook.xlsx.writeBuffer()
}

/** writeXlsxBuffer as a Blob, ready to download. */
export const writeXlsx = async (sheets) => new Blob([await writeXlsxBuffer(sheets)], { type: XLSX_MIME })
